/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useMemo } from "react"
import { isArray } from "radash"
import { Control, UseFormRegister, useWatch } from "react-hook-form"

import { CustomizeItem } from "@/lib/types"
import { cn } from "@/lib/utils"
import { Checkbox } from "@/components/ui/checkbox"
import { FormControl, FormField, FormItem } from "@/components/ui/form"
import { Tag } from "@/components/tag"

interface MenuItemCustomizeItemListProps {
  data: CustomizeItem
  currencyUnit?: string
  register: UseFormRegister<any>
  control: Control<any, any>
  hasSubmitted?: boolean
  isRestoClosed?: boolean
}

export function MenuItemCustomizeItemList({
  data,
  currencyUnit = "đ",
  hasSubmitted = false,
  control,
  isRestoClosed = false,
}: MenuItemCustomizeItemListProps) {
  // Watch for changes to this customize item's value
  const customizeValue = useWatch({
    name: `customize-${data.customize_id}`,
    control,
  })

  // Memoize validation check to prevent unnecessary re-renders
  const isValid = useMemo(() => {
    if (isArray(customizeValue)) {
      return (
        customizeValue.length >= data.customize_item_lower_limit &&
        customizeValue.length <= data.customize_item_limit
      )
    }
    return !!customizeValue
  }, [
    customizeValue,
    data.customize_item_lower_limit,
    data.customize_item_limit,
  ])

  // Memoize tag color to prevent unnecessary re-renders
  const tagColor = useMemo(() => {
    if (isValid) return "green"
    if (isArray(customizeValue)) return "red"
    return hasSubmitted ? "red" : "grey"
  }, [isValid, customizeValue, hasSubmitted])

  return (
    <div
      className="flex flex-col gap-2 pb-4"
      data-option-required={data.is_required ? (!isValid).toString() : "false"}
      data-customize-id={data.customize_id}
    >
      {/* Header section */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <span className="truncate font-semibold text-gray-800">
            {data.customize_item_name}
          </span>

          {data.is_required && (
            <span className="shrink-0">
              <Tag color={tagColor}>Bắt buộc</Tag>
            </span>
          )}
        </div>

        {/* Selection limits help text */}
        {data.is_check_box && data.customize_item_limit > 0 ? (
          <span className="text-xs text-gray-500">
            Chọn tối đa {data.customize_item_limit}
            {data.customize_item_lower_limit > 0 &&
              `, Chọn tối thiểu ${data.customize_item_lower_limit}`}
          </span>
        ) : null}
      </div>

      {/* Options section */}
      <div className="flex flex-col divide-y divide-gray-200/50">
        {data.is_check_box ? (
          <CheckboxOptionsList
            data={data}
            control={control}
            currencyUnit={currencyUnit}
            isRestoClosed={isRestoClosed}
          />
        ) : (
          <RadioOptionsList
            data={data}
            control={control}
            currencyUnit={currencyUnit}
            isRestoClosed={isRestoClosed}
          />
        )}
      </div>
    </div>
  )
}

// Component for checkbox-type options (multiple selection)
function CheckboxOptionsList({
  data,
  control,
  currencyUnit,
  isRestoClosed,
}: {
  data: CustomizeItem
  control: Control<any>
  currencyUnit: string
  isRestoClosed: boolean
}) {
  return (
    <FormField
      control={control}
      name={`customize-${data.customize_id}`}
      render={() => (
        <FormItem className="flex flex-col divide-y divide-gray-200/50">
          {data.customize_options.map((option) => (
            <FormField
              key={option.customize_option_id}
              control={control}
              name={`customize-${data.customize_id}`}
              render={({ field }) => {
                const values = field.value || []
                const isChecked = values.includes(option.customize_option_id)
                const isDisabled =
                  isRestoClosed ||
                  (!isChecked &&
                    (!option.customize_option_active ||
                      (data.customize_item_limit > 0 &&
                        isArray(values) &&
                        values.length >= data.customize_item_limit)))

                return (
                  <FormItem key={option.customize_option_id}>
                    <FormControl>
                      <label className="flex py-3 cursor-pointer">
                        <div className="flex-1">
                          <span className="line-clamp-2 text-gray-600 text-sm">
                            {option.customize_option_name}
                          </span>
                        </div>

                        <div className="ml-auto flex items-center gap-2 pl-3">
                          <span className="text-gray-700 text-sm">
                            {option.customize_display_price}
                            {currencyUnit}
                          </span>
                          <Checkbox
                            disabled={isDisabled}
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([
                                  ...values,
                                  option.customize_option_id,
                                ])
                              } else {
                                field.onChange(
                                  values.filter(
                                    (value: number) =>
                                      value !== option.customize_option_id
                                  )
                                )
                              }
                            }}
                            className="h-4 w-4"
                          />
                        </div>
                      </label>
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          ))}
        </FormItem>
      )}
    />
  )
}

// Component for radio-type options (single selection)
function RadioOptionsList({
  data,
  control,
  currencyUnit,
  isRestoClosed,
}: {
  data: CustomizeItem
  control: Control<any>
  currencyUnit: string
  isRestoClosed: boolean
}) {
  return (
    <>
      {data.customize_options.map((option) => (
        <FormField
          key={option.customize_option_id}
          control={control}
          name={`customize-${data.customize_id}`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <label className="flex py-3 cursor-pointer">
                  <div className="flex-1">
                    <span className="line-clamp-2 text-gray-600 text-sm">
                      {option.customize_option_name}
                    </span>
                  </div>

                  <div className="ml-auto flex items-center gap-2 pl-3">
                    <span className="text-gray-700 text-sm">
                      {option.customize_display_price}
                      {currencyUnit}
                    </span>

                    <div className="flex items-center">
                      <div
                        className={cn(
                          "inline-block h-4 w-4 cursor-pointer rounded-full border-2 border-gray-300 bg-white",
                          field.value === option.customize_option_id &&
                            "border-4 border-be-yellow",
                          (isRestoClosed || !data.customize_active) &&
                            "bg-gray-200"
                        )}
                      >
                        <input
                          type="radio"
                          disabled={isRestoClosed || !data.customize_active}
                          onChange={() =>
                            field.onChange(option.customize_option_id)
                          }
                          checked={field.value === option.customize_option_id}
                          value={option.customize_option_id}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </label>
              </FormControl>
            </FormItem>
          )}
        />
      ))}
    </>
  )
}
